import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatRooms(count: number): string {
  return `${count} room${count !== 1 ? "s" : ""}`;
}

export function formatOccupancy(percentage: number): string {
  return `${percentage}%`;
}

export function formatDate(
  iso: string | number | Date,
  locale: string = "en-GB",
  opts: Intl.DateTimeFormatOptions = {},
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const base: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  return d.toLocaleDateString(locale, { ...base, ...opts });
}

export function formatRelativeTime(
  iso: string | number | Date,
  locale: string = "en-GB",
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 1000 * 60 * 60 * 24 * 365],
    ["month", 1000 * 60 * 60 * 24 * 30],
    ["week", 1000 * 60 * 60 * 24 * 7],
    ["day", 1000 * 60 * 60 * 24],
    ["hour", 1000 * 60 * 60],
    ["minute", 1000 * 60],
    ["second", 1000],
  ];

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, ms] of units) {
    const val = Math.round(abs / ms);
    if (val >= 1) return rtf.format(Math.sign(diffMs) * val, unit);
  }
  return "now";
}

export function formatCurrency(
  value: number,
  currency: "EUR" | "USD" | "GBP" = "EUR",
  locale = "en-GB",
): string {
  const finalLocale =
    currency === "USD" ? "en-US" : currency === "EUR" ? "de-DE" : locale;

  return new Intl.NumberFormat(finalLocale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    currencyDisplay: "symbol",
  }).format(value);
}

export function getCurrencySymbol(
  currency: "EUR" | "USD" | "GBP" | undefined,
): string {
  if (!currency) return "€";

  try {
    const formatted = formatCurrency(1, currency, "en-US");

    return formatted.replace(/[0-9.,\s]/g, "");
  } catch (e) {
    console.warn("Failed to get currency symbol for:", currency, e);
    return currency === "USD" ? "$" : currency === "GBP" ? "£" : "€";
  }
}

export function formatNumberWithThousandsSeparator(
  value: number,
  locale: string = "en-US",
): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    value,
  );
}

export const safeArray = <T>(v: T[] | undefined | null): T[] =>
  Array.isArray(v) ? v.filter(Boolean) : [];

export function lastSavedLabel(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";

  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

export function formatPercentYearOnCost(value: number): string {
  if (Math.abs(value) < 1) {
    return (value * 100).toFixed(1) + "%";
  }

  return value.toFixed(1) + "%";
}
